import { createContext, useContext } from 'react'

import { roleCan } from './permissions'
import type { Capability, MembershipSummary, Role } from './types'

export interface WorkspaceValue {
  slug: string
  membership: MembershipSummary
  role: Role
  isPersonal: boolean
  /** UI affordance check. The server re-checks every one of these. */
  can: (capability: Capability) => boolean
  /** Builds a router path inside this workspace: `path('/settings/members')`. */
  path: (suffix?: string) => string
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null)

export const WorkspaceProvider = WorkspaceContext.Provider

/**
 * Builds the context value for one workspace.
 *
 * The active workspace is taken from the URL, never from a stored preference:
 * that is what makes every screen in the app linkable and what keeps two tabs
 * on two workspaces from fighting over one "current org" variable.
 */
export function buildWorkspace(membership: MembershipSummary): WorkspaceValue {
  const slug = membership.slug
  return {
    slug,
    membership,
    role: membership.role,
    isPersonal: membership.type === 'personal',
    can: (capability) => roleCan(membership.role, capability),
    path: (suffix = '') => `/${slug}${suffix}`,
  }
}

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext)
  if (!value) {
    throw new Error('useWorkspace must be used inside a workspace route')
  }
  return value
}

/** Same as useWorkspace, but returns null outside a workspace route. */
export function useOptionalWorkspace(): WorkspaceValue | null {
  return useContext(WorkspaceContext)
}
